import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
    IContactSelected,
    IInternalHistoricalWorkItem,
} from '@dxp/core/models';
import { Duration } from 'date-fns';
import { secondsToDuration } from '@dxp/core/helpers';
import { FormatDurationPipe, RemoveBracesPipe } from '@dxp/core/pipes';
import { TelXLPanelComponent, ContactIconComponent } from '@dxp/elements';
import { VoiceService } from '@dxp/core/services';
import { UnknownCustomer } from '@dxp/core/types';
import { VoiceCallNewService } from '../../services/voice-call-new.service';

@Component({
    selector: 'voice-recent-call',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        FormatDurationPipe,
        TelXLPanelComponent,
        ContactIconComponent,
        RemoveBracesPipe,
    ],
    templateUrl: './voice-recent-call.component.html',
    styleUrl: './voice-recent-call.component.scss',
})
export class VoiceRecentCallComponent {
    readonly #voiceService = inject(VoiceService);
    readonly #voiceCallNewService = inject(VoiceCallNewService);

    readonly callStates = {
        inbound: { key: 'inbound', label: 'Inbound', isMissedCall: false },
        outbound: { key: 'outbound', label: 'Outbound', isMissedCall: false },
        missed: { key: 'missed', label: 'Missed Call', isMissedCall: true },
    };

    selectedContact: IContactSelected = { name: '', identifier: '', type: '' };

    /**
     * Input Signals
     */
    call = input.required<IInternalHistoricalWorkItem>();
    isActiveInput = input<boolean>(false);
    contactSelected = output<IContactSelected>();

    /**
     * Writeable Signals
     */
    isActive = signal<boolean>(this.isActiveInput());
    conversation = computed(() => {
        return this.call()?.conversations[0] ?? null;
    });

    /**
     * Computed Signals
     */
    customerName = computed(() => {
        if ((this.customerPhoneNumber() ?? '').match(/\{\{(.*?)\}\}/)) {
            return 'Internal';
        }
        return (!this.conversation()?.customerFirstName &&
            !this.conversation()?.customerLastName) ||
            !this.conversation()
            ? UnknownCustomer
            : `${this.conversation().customerFirstName ?? ''} ${this.conversation().customerLastName ?? ''}`.trim();
    });
    customerNameForIcon = computed(() => {
        if ((this.customerPhoneNumber() ?? '').match(/\{\{(.*?)\}\}/)) {
            return this.customerPhoneNumber();
        }
        return (!this.conversation()?.customerFirstName &&
            !this.conversation()?.customerLastName) ||
            !this.conversation()
            ? UnknownCustomer
            : `${this.conversation().customerFirstName ?? ''} ${this.conversation().customerLastName ?? ''}`.trim();
    });
    customerPhoneNumber = computed(
        () => this.conversation()?.customerPhoneNumber ?? 'Withheld CLI',
    );
    callStatus = computed(() => {
        if (!this.conversation()) {
            return {
                ...this.callStates['inbound'],
                isUnknownCaller: true,
            };
        }
        const mappedKey =
            this.conversation().conversationEndedReason === 'Abandoned' &&
            this.call().completionCode === '<Default>'
                ? 'missed'
                : this.conversation().isOutbound
                  ? 'outbound'
                  : 'inbound';

        return {
            ...this.callStates[mappedKey],
            isUnknownCaller: this.customerName() === UnknownCustomer,
        };
    });
    callStatusClasses = computed(() => {
        const mappedCSSClasses: Record<string, string[]> = {
            inbound: ['fa-phone-arrow-down-left'],
            outbound: ['fa-phone-arrow-up-right'],
            missed: ['fa-phone-missed', 'icon-red'],
        };

        return mappedCSSClasses[this.callStatus()?.key] ?? [];
    });

    /**
     * Outputs
     */
    callSelected = output<IContactSelected>();

    selected() {
        this.isActive.set(true);

        let phoneNumber = this.customerPhoneNumber() ?? '';
        const match = phoneNumber.match(/\{\{(.*?)\}\}/);
        const custName = this.customerPhoneNumber()
            .replace(/\{+.*?\}+/g, '')
            .trim();

        if (match) {
            phoneNumber = match[1];
            this.#voiceService.contactSelected({
                ...this.selectedContact,
                name: custName,
                identifier: phoneNumber,
                type: 'agent',
                internalCall: true,
            });

            this.#voiceCallNewService.callNumber.set(custName);
            this.#voiceCallNewService.internalCostCentreChange();
            this.#voiceCallNewService.makeCall('agent');
        } else {
            this.callSelected.emit({
                ...this.selectedContact,
                name: this.customerName(),
                identifier: phoneNumber,
                type: 'agent',
                internalCall: match !== null,
            });
        }
    }

    get callSeconds(): Duration {
        return secondsToDuration(this.call().durationInSeconds);
    }
}
